import type { IconName } from "@astrum/ui";
import type { Channel, Message, MessageFlagKind } from "../../lib/api.js";

/* Types and constants shared by the chat modules. Nothing here renders. */

export type ChannelMember = {
  id: string;
  displayName: string;
  role: string;
  avatarUrl: string | null;
  department: string | null;
};

export type ChannelDetail = {
  channel: Pick<Channel, "id" | "projectId" | "name" | "topic" | "kind" | "isPrivate"> & {
    description?: string;
    archivedAt?: string | null;
    /** Whether the viewer may add people: members of a group DM can, others need canManage. */
    canAddMembers?: boolean;
    /** Whether the viewer may rename or delete it — creator or admin. */
    canManage?: boolean;
    /** The creator, who cannot be removed from their own channel. */
    createdById?: string;
    /** The viewer's own notification level for this channel; never anyone else's. */
    notificationLevel?: NotificationLevel;
  };
  members: ChannelMember[];
};

export type NotificationLevel = "all" | "mentions" | "none";

/** A message that has been optimistically appended but not yet confirmed by the server. */
export type PendingMessage = Message & { pending?: boolean; failed?: boolean };

/** One file on a message, as the thread and the forward dialog see it. */
export type MessageAttachment = NonNullable<Message["attachments"]>[number];

/** What "Forward to…" needs to know about the file being forwarded. */
export type ForwardableAttachment = Pick<MessageAttachment, "id" | "fileName" | "contentType" | "sizeBytes">;

/**
 * A file being uploaded, shown optimistically in the thread before the real
 * message exists. `progress` is 0..1 while the body streams; `failed` flips on
 * an error so the bubble can offer a retry/dismiss. `cancel` aborts the XHR.
 */
export type UploadingFile = {
  clientId: string;
  fileName: string;
  sizeBytes: number;
  progress: number;
  failed: boolean;
  /** Why the upload failed, taken from the server's response — shown on the
   *  failed bubble so the person sees the actual reason (scanner down, file too
   *  large, wrong type) instead of a bare "Upload failed". */
  error?: string;
  cancel: () => void;
};

export type SearchablePerson = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  jobTitle: string | null;
  department: string | null;
};

/** The message page size, matching GET /channels/:id/messages' default. */
export const MESSAGE_PAGE = 50;

/**
 * Types the preview endpoint will serve inline, mirrored here so the client
 * does not request a preview it knows will be refused. Kept in step with
 * `INLINE_IMAGE_TYPES` in the API's file routes — notably no SVG, which is a
 * document that can carry script.
 */
export const INLINE_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

/**
 * Every way of slicing the sidebar, Lark-style: some are subsets of the
 * already-fetched channel list (client-side), some are cross-channel message
 * lists (a fetch of their own), and Labels replaces the list with a manager.
 */
export type FilterKind =
  | "chats"
  | "unread"
  | "flagged"
  | "todo"
  | "done"
  | "mentions"
  | "labels"
  | "dms"
  | "groups"
  | "threads"
  | "saved";

export const FILTER_TABS: { kind: FilterKind; label: string; icon: IconName }[] = [
  { kind: "chats", label: "Chats", icon: "chat" },
  { kind: "unread", label: "Unread", icon: "bell" },
  { kind: "flagged", label: "Flagged", icon: "flag" },
  { kind: "todo", label: "To-do", icon: "check" },
  { kind: "done", label: "Done", icon: "check" },
  { kind: "mentions", label: "Mentions", icon: "at" },
  { kind: "labels", label: "Labels", icon: "pin" },
  { kind: "dms", label: "DMs", icon: "people" },
  { kind: "groups", label: "Groups", icon: "work" },
  { kind: "threads", label: "Threads", icon: "reply" },
  { kind: "saved", label: "Saved", icon: "archive" },
];

/** The three filters backed by a personal flag on a message, not a channel subset. */
export const FLAG_FILTER_KIND: Partial<Record<FilterKind, MessageFlagKind>> = {
  flagged: "flag",
  todo: "todo",
  done: "done",
};
